import { getSessionOnce } from "@/lib/session-cache";
import { signOutToLogin } from "@/lib/auth-logout";
import { isSuperAdmin } from "@/lib/auth";
import { accessTokenIsSuperAdmin } from "@/lib/jwt-claims";
import { isAuthenticatedSession } from "@/lib/session";
import { getActingTenantHeaders } from "./acting-tenant";

// Empty => same-origin (/api/v1/...) behind Traefik. Set NEXT_PUBLIC_API_URL for direct
// backend access in local dev without the compose ingress (e.g. http://localhost:8000).
const API_URL = process.env.NEXT_PUBLIC_API_URL ?? "";

function isAdminPath(path: string): boolean {
  return path.startsWith("/api/v1/admin/");
}

function actingTenantHeadersForSession(
  path: string,
  accessToken: string | undefined,
  roles: string[] | undefined,
): Record<string, string> {
  if (isAdminPath(path)) {
    return {};
  }
  const canActAsOrg =
    isSuperAdmin(roles) || accessTokenIsSuperAdmin(accessToken);
  if (!canActAsOrg) {
    return {};
  }
  return getActingTenantHeaders();
}

async function authFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const session = await getSessionOnce();
  if (!isAuthenticatedSession(session)) {
    void signOutToLogin();
    throw new Error("Session expired");
  }
  const headers: Record<string, string> = {
    ...(session?.accessToken
      ? { Authorization: `Bearer ${session.accessToken}` }
      : {}),
    ...(options.body instanceof FormData
      ? {}
      : { "Content-Type": "application/json" }),
    ...actingTenantHeadersForSession(
      path,
      session?.accessToken,
      session?.user?.roles,
    ),
    ...(options.headers as Record<string, string> | undefined),
  };

  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers,
  });
  if (res.status === 401) {
    void signOutToLogin();
    throw new Error("Session expired");
  }

  if (!res.ok) {
    let message = `API error ${res.status} (${path})`;
    try {
      const err = await res.json();
      const detail = err.detail;
      if (typeof detail === "string") {
        message = `${detail} (${path})`;
      } else if (detail) {
        message = `${message}: ${JSON.stringify(detail)}`;
      }
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

export type ApiGetResult<T> = {
  data: T;
  /** True when the server returned cached data and is refreshing in the background. */
  stale: boolean;
};

async function authFetchWithStale<T>(
  path: string,
  options: RequestInit = {},
): Promise<ApiGetResult<T>> {
  const session = await getSessionOnce();
  if (!isAuthenticatedSession(session)) {
    void signOutToLogin();
    throw new Error("Session expired");
  }
  const headers: Record<string, string> = {
    ...(session?.accessToken
      ? { Authorization: `Bearer ${session.accessToken}` }
      : {}),
    ...(options.body instanceof FormData
      ? {}
      : { "Content-Type": "application/json" }),
    ...actingTenantHeadersForSession(
      path,
      session?.accessToken,
      session?.user?.roles,
    ),
    ...(options.headers as Record<string, string> | undefined),
  };

  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers,
  });
  if (res.status === 401) {
    void signOutToLogin();
    throw new Error("Session expired");
  }

  if (!res.ok) {
    let message = `API error ${res.status} (${path})`;
    try {
      const err = await res.json();
      const detail = err.detail;
      if (typeof detail === "string") {
        message = `${detail} (${path})`;
      } else if (detail) {
        message = `${message}: ${JSON.stringify(detail)}`;
      }
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  if (res.status === 204) {
    return { data: undefined as T, stale: false };
  }
  const stale = res.headers.get("X-Data-Stale") === "true";
  return { data: await res.json(), stale };
}

function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  const utfMatch = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utfMatch?.[1]) {
    try {
      return decodeURIComponent(utfMatch[1]);
    } catch {
      return utfMatch[1];
    }
  }
  const quoted = /filename="([^"]+)"/i.exec(header);
  if (quoted?.[1]) return quoted[1];
  const plain = /filename=([^;]+)/i.exec(header);
  return plain?.[1]?.trim() ?? null;
}

async function authDownload(
  path: string,
  fallbackFilename: string,
): Promise<void> {
  const session = await getSessionOnce();
  if (!isAuthenticatedSession(session)) {
    void signOutToLogin();
    throw new Error("Session expired");
  }
  const headers: Record<string, string> = {
    ...(session?.accessToken
      ? { Authorization: `Bearer ${session.accessToken}` }
      : {}),
    ...actingTenantHeadersForSession(
      path,
      session?.accessToken,
      session?.user?.roles,
    ),
  };

  const res = await fetch(`${API_URL}${path}`, { headers });
  if (res.status === 401) {
    void signOutToLogin();
    throw new Error("Session expired");
  }
  if (!res.ok) {
    let message = `API error ${res.status} (${path})`;
    try {
      const err = await res.json();
      const detail = err.detail;
      if (typeof detail === "string") {
        message = `${detail} (${path})`;
      } else if (detail) {
        message = `${message}: ${JSON.stringify(detail)}`;
      }
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }

  const blob = await res.blob();
  const filename =
    filenameFromContentDisposition(res.headers.get("Content-Disposition")) ??
    fallbackFilename;
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export const api = {
  get: <T>(path: string) => authFetch<T>(path),
  getWithStale: <T>(path: string) => authFetchWithStale<T>(path),
  post: <T>(path: string, body: unknown) =>
    authFetch<T>(path, { method: "POST", body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    authFetch<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    authFetch<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  delete: (path: string) =>
    authFetch<void>(path, { method: "DELETE" }),
  upload: <T>(path: string, formData: FormData) =>
    authFetch<T>(path, { method: "POST", body: formData }),
  download: (path: string, fallbackFilename: string) =>
    authDownload(path, fallbackFilename),
};
